// Shared httpsCallable factory for the admin console.
//
// WHY THIS EXISTS — the `undefined` → `null` trap:
// The Firebase JS SDK serialises callable payloads with its own `encode()`,
// which begins `if (data == null) return null;`. That `==` is loose, so an
// explicitly-present key holding `undefined` is sent over the wire as `null`
// (it is NOT dropped the way `JSON.stringify` would drop it).
//
// Our server schemas use `z.…optional()`, which accepts `undefined` but
// REJECTS `null`. So a perfectly ordinary wrapper like
//     listInvoices(filter || undefined, 200)  ->  { status: undefined, limit: 200 }
// arrives at the callable as `{ status: null, limit: 200 }` and fails
// validation with "Invalid input for padminListInvoices."
//
// Fix: drop keys whose value is `undefined` before handing the payload to the
// SDK. We deliberately do NOT drop explicit `null` — some callables use `null`
// as a real value (e.g. `assignee: null` to unassign a conversation), and
// conflating the two would silently break those.
import { httpsCallable, type HttpsCallableResult } from 'firebase/functions';
import { functions } from './firebase';

/** Recursively remove `undefined`-valued keys. Explicit `null` is preserved. */
export function stripUndefined<T>(value: T): T {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((v) => stripUndefined(v)) as unknown as T;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (v !== undefined) out[k] = stripUndefined(v);
  }
  return out as T;
}

/**
 * Drop-in replacement for `httpsCallable(functions, name)` that sanitises the
 * payload first. Same call signature and same `{ data }` result shape.
 */
export function call<I, O>(name: string): (data: I) => Promise<HttpsCallableResult<O>> {
  const fn = httpsCallable<I, O>(functions, name);
  return (data: I) => fn(stripUndefined(data));
}
